<script lang="ts">
  import { Hexagon, MapPin } from "lucide-svelte";
  import { locationStore } from "$lib/stores/location.svelte";
  import { formatDistance } from "$lib/helpers/format-distance";
  import type { PlaceResult } from "$lib/types/place.types";

  interface Props {
    items: PlaceResult[];
    isLoading: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
    onItemClick: (item: PlaceResult) => void;
  }

  let { items, isLoading, hasMore, onLoadMore, onItemClick }: Props = $props();

  const typeLabels: Record<string, string> = {
    // Settlements
    city: "Város",
    town: "Kisváros",
    village: "Község",
    hamlet: "Falu",
    suburb: "Külváros",
    neighbourhood: "Városrész",
    quarter: "Negyed",
    district: "Kerület",

    // Administrative
    administrative: "Közigazgatási terület",
    municipality: "Önkormányzat",
    county: "Megye",
    region: "Régió",

    // Natural features
    lake: "Tó",
    park: "Park",
    nature_reserve: "Természetvédelmi terület",
    national_park: "Nemzeti park",
    forest: "Erdő",
    peak: "Hegycsúcs",
    mountain_range: "Hegység",
    hill: "Domb",
    valley: "Völgy",
    island: "Sziget",
    beach: "Strand",

    // Water features
    river: "Folyó",
    stream: "Patak",
    canal: "Csatorna",
    waterfall: "Vízesés",
    spring: "Forrás",

    // Tourism & Culture
    attraction: "Látnivaló",
    museum: "Múzeum",
    castle: "Vár",
    palace: "Palota",
    monument: "Emlékmű",
    memorial: "Emlékhely",
    archaeological_site: "Régészeti lelőhely",
    heritage_site: "Örökségi helyszín",
    viewpoint: "Kilátó",
    zoo: "Állatkert",
    theme_park: "Vidámpark",

    // Religious
    church: "Templom",
    cathedral: "Székesegyház",
    chapel: "Kápolna",
    monastery: "Kolostor",
    synagogue: "Zsinagóga",
    mosque: "Mecset",

    // Infrastructure
    station: "Állomás",
    railway_station: "Vasútállomás",
    bus_station: "Buszállomás",
    airport: "Repülőtér",
    aerodrome: "Repülőtér",
    port: "Kikötő",
    ferry_terminal: "Kompkikötő",

    // Commercial
    mall: "Bevásárlóközpont",
    marketplace: "Piac",
    shopping_centre: "Bevásárlóközpont",

    // Education
    university: "Egyetem",
    college: "Főiskola",
    school: "Iskola",

    // Health
    hospital: "Kórház",
    clinic: "Klinika",

    // Sports & Recreation
    stadium: "Stadion",
    sports_centre: "Sportközpont",
    swimming_pool: "Uszoda",
    golf_course: "Golfpálya",

    // Other
    place: "Hely",
    locality: "Helység",
    farm: "Tanya",
    industrial: "Ipari terület",
    commercial: "Kereskedelmi terület",
    residential: "Lakóterület",
    cemetery: "Temető",
  };

  function getPlaceTypeLabel(type: string): string {
    return typeLabels[type] || type;
  }
</script>

<div class="relative rounded-none overflow-hidden">
  {#if items.length === 0 && !isLoading}
    <div class="relative text-center py-16 px-8">
      <div class="inline-block mb-4">
        <MapPin class="w-12 h-12 text-muted-foreground" />
      </div>
      <p class="text-foreground text-lg font-bold">[ Nincs találat ]</p>
    </div>
  {:else}
    <div class="relative space-y-2 pt-1 px-4">
      {#each items as item (item.id)}
        {@const distance = locationStore.userLocation
          ? formatDistance(
              locationStore.calculateDistance(
                { lat: item.lat, lng: item.lon },
                locationStore.userLocation
              )
            )
          : null}
        <button
          class="w-full text-left border-2 border-foreground rounded-md bg-white shadow-black
                 hover:shadow-[2px_2px_0px_0px] hover:shadow-foreground p-3
                 hover:-translate-y-0.5 hover:bg-secondary/70 transition-all group"
          onclick={() => onItemClick(item)}
        >
          <div class="flex gap-4">
            <!-- Icon on the left -->
            <img src="/assets/icons/station.svg" alt={item.displayName} />

            <!-- Content area -->
            <div class="flex-1 min-w-0">
              <!-- Place name -->
              <div class="flex items-center gap-2 mb-1">
                <h3 class="text-lg font-extrabold text-foreground truncate">
                  {item.name}
                </h3>
                <span
                  class="text-sm text-muted-foreground font-bold whitespace-nowrap"
                >
                  {getPlaceTypeLabel(item.type)}
                </span>
              </div>

              <!-- County/Region info -->
              {#if item.address.county || item.address.state}
                <p class="text-sm text-foreground/80 mb-2">
                  {item.address.county || ""}{item.address.county &&
                  item.address.state
                    ? ", "
                    : ""}{item.address.state || ""}
                </p>
              {/if}

              <!-- Distance on bottom right -->
              <div class="flex justify-between items-end">
                <span class="text-sm text-muted-foreground">
                  Magyarország
                </span>
                {#if distance}
                  <div
                    class="text-xs font-mono text-muted-foreground bg-input px-2 py-1 border border-foreground"
                  >
                    {distance}
                  </div>
                {/if}
              </div>
            </div>
          </div>
        </button>
      {/each}
    </div>
    {#if hasMore}
      <div class="relative border-t-2 border-border pt-4 pb-2 px-4">
        <button
          class="w-full py-3 px-6 text-foreground font-bold border-2 border-foreground
                 hover:bg-accent/10 disabled:opacity-50 disabled:cursor-not-allowed
                 transition-all hover:shadow-[2px_2px_0px_0px] hover:shadow-foreground hover:-translate-y-0.5
                 active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
          onclick={onLoadMore}
          disabled={isLoading}
        >
          {#if isLoading}
            <span class="inline-flex items-center gap-2">
              <div class="relative animate-spin-slow opacity-60">
                <Hexagon
                  class="w-32 h-32 text-black fill-primary"
                  strokeWidth={2.5}
                />
                <div class="absolute inset-0 flex items-center justify-center">
                  <img src="/assets/ui-icons/beeco-logo.svg" alt="Logo" />
                </div>
              </div>
              <span
                class="inline-block w-4 h-4 border-2 border-foreground border-t-transparent rounded-full animate-spin"
              ></span>
              [ Betöltés... ]
            </span>
          {:else}
            [ → További eredmények ]
          {/if}
        </button>
      </div>
    {/if}
  {/if}
</div>
